import { useTranslation } from 'react-i18next'
import { DataItem } from 'scrivito'

import { Button } from '@/Atomic/Molecules/Button'
import { unLinkDataItem } from '@/Atomic/Organisms/RelationalSublist'
import { SublistItem, SublistItemContent } from '@/Atomic/Templates/Sublist'
import { DataClassName } from '@/Data/const'
import { SchemaAttributes } from '@/Data/typesV2'
import { useUndoableAction } from '@/hooks/useUndoableAction'
import { getEnumDisplayValue } from '@/utils/CRUD/read/enum/getDisplayValue'
import { getSchemaAttributes } from '@/utils/CRUD/read/schema'
import { getSchemaName } from '@/utils/dataClassMap'
import { formatDateTime } from '@/utils/formatDateTime'

interface QuoteSublistItemProps {
  quote: DataItem
  relation?: DataItem
  flash?: boolean
  onOpen: (quote: DataItem) => void
}

const formatAmount = (rawValue: number | undefined) => {
  if (typeof rawValue !== 'number' || !Number.isFinite(rawValue)) return ''
  return Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rawValue)
}

export const buildQuoteSubtitle = (
  quote: DataItem,
  schemaAttributes?: SchemaAttributes,
) => {
  const totalPriceLabel = schemaAttributes?.totalPrice?.title || 'Revenue'
  const dateLabel = schemaAttributes?.quoteAt?.title || 'Quote date'
  const statusLabel = schemaAttributes?.status?.title || 'Status'
  const typeLabel = schemaAttributes?.type?.title || 'Type'

  const status = quote.get('status') as string
  const type = quote.get('type') as string
  const dateValue = quote.get('quoteAt') as Date | undefined
  const totalPrice = quote.get('totalPrice') as number | undefined
  const currency = (quote.get('totalPriceCurrency') as string) || ''

  const statusDisplayValue =
    getEnumDisplayValue(schemaAttributes?.status?.enumValues, status) ?? status
  const typeDisplayValue =
    getEnumDisplayValue(schemaAttributes?.type?.enumValues, type) ?? type

  const amount = formatAmount(totalPrice)
  const amountWithCurrency = [amount, currency].filter(Boolean).join(' ')

  return [
    amountWithCurrency ? `${totalPriceLabel}: ${amountWithCurrency}` : '',
    dateValue ? `${dateLabel}: ${formatDateTime(dateValue, 'PP')}` : '',
    statusDisplayValue ? `${statusLabel}: ${statusDisplayValue}` : '',
    typeDisplayValue ? `${typeLabel}: ${typeDisplayValue}` : '',
  ]
    .filter(Boolean)
    .join(' · ')
}

export const QuoteSublistItem: React.FC<QuoteSublistItemProps> = ({
  quote,
  relation,
  flash,
  onOpen,
}) => {
  const { t } = useTranslation()
  const { isHidden, run } = useUndoableAction()
  const { schemaAttributes } = getSchemaAttributes(
    getSchemaName(DataClassName.QUOTE),
  )

  const displayTitle =
    (quote.get('displayTitle') as string) ||
    (quote.get('keyword') as string) ||
    ''

  if (relation && isHidden(relation.id())) return null

  return (
    <SublistItem flash={flash}>
      <SublistItemContent
        avatarIcon="invoice"
        title={displayTitle}
        subtitle={buildQuoteSubtitle(quote, schemaAttributes)}
        onClick={() => onOpen(quote)}
        showButton="onHover"
        button={
          relation ? (
            <Button
              variant="icon"
              icon="cross"
              tooltip={t('common.remove')}
              onClick={() => {
                const relationId = relation.id()

                run({
                  id: relationId,
                  onAction: ({ onError, onUndo }) =>
                    unLinkDataItem({
                      relationDataItem: relation,
                      removedListItem: quote,
                      onError,
                      onUndo,
                    }),
                })
              }}
            />
          ) : undefined
        }
      />
    </SublistItem>
  )
}
